import { MenuItem, Alert, Box, Card, Stack, Tooltip, Typography, Link } from "@mui/material";
import _ from "lodash";
import { Decision } from "../models";
import { Formik, Field as FormikField } from "formik";
import { TextField as FmuiTextField } from "formik-mui";
import { LoadingButton } from "@mui/lab";
import { TableFormValues } from "decisions/DecisionStageTab";
import { useMutation, useQuery } from "@apollo/client";
import { AvailableMeetingsQuery, CommitteeMeeting, FetchCommitteeMeetingToEnterDecisionsQuery } from "committee-meetings";
import { TablePrMutation } from "decisions/queries";
import { DateTime } from "luxon";
import { PracticeReview } from "practice-reviews";
import { useNotifications } from "notifications";
import * as Yup from "yup";
import { meetingStyles } from "styles/common";
import { makeStyles } from "makeStyles";
import {
  getRouteForRegistrationCommitteeItem,
  RegistrationCommitteeItemTabs
} from "registration-committee-items/RegistrationCommitteeItemScreen";
import { Link as RouterLink } from "react-router-dom";
import { RegistrationCommitteeItem } from "registration-committee-items";

const useStyles = makeStyles()((theme) => ({
  ...meetingStyles(theme)
}));

export const PrcReviewTabler: React.FunctionComponent<{
  practiceReview: PracticeReview;
  committeeDecision: Decision | null;
}> = (props) => {
  const { classes, cx } = useStyles();
  const notifications = useNotifications();
  const [tablePrMutate, tablePrMutation] = useMutation<
    {
      practiceReview: {
        table: PracticeReview;
      };
    },
    {
      practiceReviewId: number;
      committeeMeetingId: number;
      reason: string;
    }
  >(TablePrMutation, {
    refetchQueries: [
      {
        query: FetchCommitteeMeetingToEnterDecisionsQuery,
        variables: {
          meetingId: props.practiceReview.committeeMeeting?.id ?? 0,
          meetingDate: props.practiceReview.committeeMeeting?.meetingDate ?? DateTime.now().toISODate()
        }
      }
    ]
  });

  async function tablePr(values: TableFormValues) {
    const mutationResult = await tablePrMutate({
      variables: {
        practiceReviewId: props.practiceReview.id,
        committeeMeetingId: values.committeeMeetingId!,
        reason: values.reason
      }
    });

    if (mutationResult.data?.practiceReview.table.id) {
      notifications.success("PR tabled.");
    }
  }

  const tablingFormValidationSchema = Yup.object({
    committeeMeetingId: Yup.mixed().required("Select a meeting."),
    reason: Yup.string().trim().required("Enter the reason the PR was tabled.")
  });

  const availableMeetingsQuery = useQuery<{ availableMeetings: CommitteeMeeting[] }>(AvailableMeetingsQuery);
  const meetingsThatPrCanBeTabledTo =
    availableMeetingsQuery.data?.availableMeetings.filter((m) => m.id !== props.practiceReview.committeeMeeting?.id) ?? [];

  return (
    <Tooltip title={props.practiceReview.isFinalReportReviewedByDirector ? "The final report has already been marked as reviewed." : ""}>
      <span>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3" gutterBottom>
            Table to Future Meeting
          </Typography>
          {props.practiceReview.isPprpProgramReview ? (
            <Typography variant="body2">
              This is a PPRP program review. To table it to a future meeting, use the tabling feature on its related{" "}
              {props.practiceReview?.bestPprpProgramReviewRegComItem?.id ? (
                <Link
                  to={getRouteForRegistrationCommitteeItem(
                    props.practiceReview?.bestPprpProgramReviewRegComItem,
                    RegistrationCommitteeItemTabs.Decision
                  )}
                  component={RouterLink}>
                  Registration Committee Item
                </Link>
              ) : (
                "Registration Committee Item"
              )}
              .
            </Typography>
          ) : (
            <>
              {meetingsThatPrCanBeTabledTo.length === 0 && <Alert color="warning">There are no meetings eligible to table PRs to.</Alert>}
              <Formik
                initialValues={{ committeeMeetingId: null, reason: "" } as TableFormValues}
                validationSchema={tablingFormValidationSchema}
                onSubmit={tablePr}>
                {(formikProps) => (
                  <>
                    <FormikField
                      component={FmuiTextField}
                      select
                      name="committeeMeetingId"
                      label="Committee Meeting"
                      fullWidth
                      required
                      disabled={
                        formikProps.isSubmitting ||
                        props.practiceReview.isFinalReportReviewedByDirector ||
                        meetingsThatPrCanBeTabledTo.length === 0 ||
                        props.committeeDecision?.signedOff
                      }
                      error={formikProps.touched.committeeMeetingId && Boolean(formikProps.errors.committeeMeetingId)}
                      helperText={formikProps.touched.committeeMeetingId && formikProps.errors.committeeMeetingId}>
                      {_.orderBy(meetingsThatPrCanBeTabledTo, (m) => m.meetingDate).map((meeting) => (
                        <MenuItem value={meeting.id}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            {meeting.name}
                            <span
                              className={cx(classes.meetingCapacity, {
                                [classes.meetingOverCapacity]: meeting.assignedReviews.length >= meeting.reviewQuota
                              })}>{`${meeting.assignedReviews.length}/${meeting.reviewQuota}`}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </FormikField>

                    <FormikField
                      component={FmuiTextField}
                      name="reason"
                      label="Reason"
                      required
                      multiline
                      fullWidth
                      helperText="This will appear in the meeting minutes."
                      disabled={
                        formikProps.isSubmitting ||
                        props.practiceReview.isFinalReportReviewedByDirector ||
                        meetingsThatPrCanBeTabledTo.length === 0 ||
                        props.committeeDecision?.signedOff
                      }
                    />

                    <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                      <LoadingButton
                        variant="outlined"
                        color="primary"
                        onClick={async () => {
                          await formikProps.submitForm();

                          if (formikProps.isValid) {
                            formikProps.resetForm();
                          }
                        }}
                        loading={tablePrMutation.loading}
                        disabled={
                          props.practiceReview.isFinalReportReviewedByDirector ||
                          meetingsThatPrCanBeTabledTo.length === 0 ||
                          props.committeeDecision?.signedOff
                        }>
                        Table
                      </LoadingButton>
                    </Stack>
                  </>
                )}
              </Formik>
            </>
          )}
        </Card>
      </span>
    </Tooltip>
  );
};
